import * as THREE from "three";
import { ScanValidation } from "./scan_validation";
import { Colors, Colormaps } from "../colors";

export class LibraryAssociation extends ScanValidation {
  constructor(element, data) {
    super(element, data);

    this.data = data;
    this.bounds = new THREE.Box3();
    this.boundsSize = new THREE.Vector3();
    this.boundsCenter = new THREE.Vector3();
    this.gaugeUpDir = new THREE.Vector3();
    this.newCameraPos = new THREE.Vector3();
    this.libraryAssociationEnabled = false;

    this.libraries = null;
    this.gaugeConstructedLibraries = null;
    this.narrowScanBodyConstructedLibraries = null;
  }

  dispose() {
    super.dispose();
    this.clearLibraryAssociation();
  }

  clearLibraryAssociation() {
    if (this.libraries) {
      for (const library of this.libraries.children) {
        library.dispose();
      }
      this.libraries.clear();
    }
    if (this.gaugeConstructedLibraries) {
      for (const library of this.gaugeConstructedLibraries.children) {
        library.dispose();
      }
      this.gaugeConstructedLibraries.clear();
    }
    if (this.narrowScanBodyConstructedLibraries) {
      for (const library of this.narrowScanBodyConstructedLibraries.children) {
        library.dispose();
      }
      this.narrowScanBodyConstructedLibraries.clear();
    }
  }

  zoomToGauge(index) {
    if (!this.libraryAssociationEnabled) {
      return;
    }

    const scanGauge = this.scanGauges.children[index];
    const topGaugePlane =
      this.data.fixedScan.scanGauges[index].bestFitPlanes[0].plane;

    this.bounds.setFromObject(scanGauge);
    this.bounds.getSize(this.boundsSize);
    this.bounds.getCenter(this.boundsCenter);
    this.gaugeUpDir.set(
      topGaugePlane.normal[0],
      topGaugePlane.normal[1],
      topGaugePlane.normal[2]
    );
    const maxDim = Math.max(
      this.boundsSize.x,
      this.boundsSize.y,
      this.boundsSize.z
    );
    const fov = this.camera.fov * (Math.PI / 180);
    const cameraDistance = maxDim / (2 * Math.tan(fov / 2));
    this.newCameraPos
      .copy(this.boundsCenter)
      .add(this.gaugeUpDir.clone().normalize().multiplyScalar(cameraDistance));
    this.moveToAndLookAt(this.newCameraPos, this.boundsCenter);

    for (let i = 0; i < this.scanGauges.children.length; i++) {
      const color = i === index ? Colors.highlight : Colors.primary;
      this.scanGauges.children[i].setUniformColor(color);
    }
  }

  setLibraryAssociationData(data) {
    this.clearLibraryAssociation();

    this.libraries = new THREE.Group();
    this.gaugeConstructedLibraries = new THREE.Group();
    const scanGaugeLibrariesCount =
      data.librariesCombinedScanComparison.libraryComparisons.length;
    for (let i = 0; i < scanGaugeLibrariesCount; i++) {
      const libraryData = {
        positions: data.scanGauges.meshes.planesMeshes[i].vertexPositions,
        normals: data.scanGauges.meshes.planesMeshes[i].vertexNormals,
        indices: data.scanGauges.meshes.planesMeshes[i].indices,
        distances:
          data.librariesCombinedScanComparison.libraryComparisons[i].distances,
      };
      const colliding = data.librariesCombinedScanComparison.collisions
        .flatMap((c) => c.gaugeIndices)
        .includes(i);
      const constructedLibraryData = {
        positions: data.scanGauges.meshes.constructedMeshes[i].vertexPositions,
        pass: data.librariesCombinedScanComparison.libraryComparisons[i].passed,
        colliding: colliding,
      };
      this.libraries.add(new Library(libraryData));
      this.gaugeConstructedLibraries.add(
        new GaugeConstructedLibrary(constructedLibraryData)
      );
    }

    this.narrowScanBodyConstructedLibraries = new THREE.Group();
    const narrowScanBodyLibrariesCount =
      data.librariesCombinedScanComparison.narrowScanBodyComparisons.length;
    for (let i = 0; i < narrowScanBodyLibrariesCount; i++) {
      const constructedLibraryData = {
        positions:
          data.narrowScanBodies.meshes.constructedMeshes[i].vertexPositions,
        normals:
          data.narrowScanBodies.meshes.constructedMeshes[i].vertexNormals,
        indices: data.narrowScanBodies.meshes.constructedMeshes[i].indices,
        pass: data.librariesCombinedScanComparison.narrowScanBodyComparisons[i]
          .passed,
      };
      this.narrowScanBodyConstructedLibraries.add(
        new NarrowScanBodyConstructedLibrary(constructedLibraryData)
      );
    }

    this.scene.add(
      this.libraries,
      this.gaugeConstructedLibraries,
      this.narrowScanBodyConstructedLibraries
    );
  }

  enableLibraryAssociation(enable) {
    this.libraryAssociationEnabled = enable;
    if (this.libraryAssociationEnabled) {
      this.controls.enabled = false;
      for (const scanGauge of this.scanGauges.children) {
        scanGauge.setUniformColorMaterial();
        scanGauge.setUniformColor(Colors.primary);
      }
    } else {
      this.controls.enabled = true;
      for (const scanGauge of this.scanGauges.children) {
        scanGauge.setColorBufferMaterial();
      }
    }
    this.lookAtScene();
  }

  setGaugeLibrariesVisible(visible) {
    if (this.libraries) {
      this.libraries.visible = visible;
    }
  }

  setConstructedGaugeLibrariesVisible(visible) {
    if (this.gaugeConstructedLibraries) {
      this.gaugeConstructedLibraries.visible = visible;
    }
  }

  setConstructedNarrowScanBodyLibrariesVisible(visible) {
    if (this.narrowScanBodyConstructedLibraries) {
      this.narrowScanBodyConstructedLibraries.visible = visible;
    }
  }
}

class Library extends THREE.Object3D {
  constructor(libraryData) {
    super();

    this.material = new THREE.MeshStandardMaterial({
      side: THREE.DoubleSide,
      vertexColors: true,
      roughness: 0.5,
      metalness: 0.4,
    });

    this.geometry = new THREE.BufferGeometry();

    const positions = new Float32Array(libraryData.positions);
    this.geometry.setAttribute(
      "position",
      new THREE.BufferAttribute(positions, 3)
    );

    const normals = new Float32Array(libraryData.normals);
    this.geometry.setAttribute("normal", new THREE.BufferAttribute(normals, 3));

    const indices = new Uint32Array(libraryData.indices);
    this.geometry.setIndex(new THREE.BufferAttribute(indices, 1));

    this.colorBuffer = new THREE.BufferAttribute(
      new Float32Array(positions.length),
      3
    ).setUsage(THREE.DynamicDrawUsage);

    const verticesCount = this.geometry.getAttribute("position").count;
    const heatmapColor = new THREE.Color();
    for (let i = 0; i < verticesCount; ++i) {
      Colormaps.turbo(heatmapColor, libraryData.distances[i], 0.0, 0.1);
      this.colorBuffer.array[i * 3] = heatmapColor.r;
      this.colorBuffer.array[i * 3 + 1] = heatmapColor.g;
      this.colorBuffer.array[i * 3 + 2] = heatmapColor.b;
    }

    this.geometry.setAttribute("color", this.colorBuffer);

    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.add(this.mesh);
  }

  dispose() {
    this.material.dispose();
    this.geometry.dispose();
  }
}

class GaugeConstructedLibrary extends THREE.Object3D {
  constructor(libraryData) {
    super();

    this.material = new THREE.MeshStandardMaterial({
      color:
        libraryData.pass && !libraryData.colliding
          ? Colors.primary
          : Colors.warning,
      side: THREE.DoubleSide,
      roughness: 0.5,
      metalness: 0.4,
    });

    this.geometry = new THREE.BufferGeometry();

    const positions = new Float32Array(libraryData.positions);
    this.geometry.setAttribute(
      "position",
      new THREE.BufferAttribute(positions, 3)
    );
    this.geometry.computeVertexNormals();

    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.add(this.mesh);
  }

  dispose() {
    this.material.dispose();
    this.geometry.dispose();
  }
}

class NarrowScanBodyConstructedLibrary extends THREE.Object3D {
  constructor(libraryData) {
    super();

    this.material = new THREE.MeshStandardMaterial({
      color: libraryData.pass ? Colors.primary : Colors.warning,
      side: THREE.DoubleSide,
      roughness: 0.5,
      metalness: 0.4,
    });

    this.geometry = new THREE.BufferGeometry();

    const positions = new Float32Array(libraryData.positions);
    this.geometry.setAttribute(
      "position",
      new THREE.BufferAttribute(positions, 3)
    );

    const normals = new Float32Array(libraryData.normals);
    this.geometry.setAttribute("normal", new THREE.BufferAttribute(normals, 3));

    const indices = new Uint32Array(libraryData.indices);
    this.geometry.setIndex(new THREE.BufferAttribute(indices, 1));

    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.add(this.mesh);
  }

  dispose() {
    this.material.dispose();
    this.geometry.dispose();
  }
}
